import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inputCls } from "@/lib/billing";
import { Btn } from "@/components/PortalPage";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const TECH_COLORS = ["#4CCC06", "#39A0FF", "#FFB800", "#FF6B9A", "#A78BFA", "#2DD4BF", "#F97316"];
export const techColor = (techs, id) => TECH_COLORS[Math.max(0, techs.findIndex((t) => t.id === id)) % TECH_COLORS.length];
export const pad = (n) => String(n).padStart(2, "0");
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const toLocal = (d) => `${ymd(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
export const parseLocal = (s) => (s ? new Date(`${s}:00`) : null);
export const fmtTime = (s) => parseLocal(s)?.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export const useTechs = () => {
  const [techs, setTechs] = useState([]);
  useEffect(() => { api("/billing/technicians").then(setTechs).catch(() => {}); }, []);
  return techs;
};

export const ScheduleFields = ({ v, setV, techs, hasEmail }) => {
  const set = (k, x) => setV({ ...v, [k]: x });
  const date = v.start_at?.slice(0, 10) || "";
  const setStart = (d, t) => {
    if (!d) return setV({ ...v, start_at: null, end_at: null });
    const start = `${d}T${t || v.start_at?.slice(11) || "09:00"}`;
    const dur = v.start_at && v.end_at ? parseLocal(v.end_at) - parseLocal(v.start_at) : 2 * 3600e3;
    setV({ ...v, start_at: start, end_at: toLocal(new Date(parseLocal(start).getTime() + Math.max(dur, 15 * 60e3))) });
  };
  const toggleTech = (id) => set("technician_ids", v.technician_ids.includes(id) ? v.technician_ids.filter((x) => x !== id) : [...v.technician_ids, id]);
  return (
    <div className="space-y-3" data-testid="schedule-fields">
      <div className="grid gap-2 md:grid-cols-4">
        <label className="text-xs text-[#888]">Date<input className={inputCls} type="date" value={date} onChange={(e) => setStart(e.target.value)} data-testid="sched-date" /></label>
        <label className="text-xs text-[#888]">Start time<input className={inputCls} type="time" disabled={!date} value={v.start_at?.slice(11) || ""} onChange={(e) => setStart(date, e.target.value)} data-testid="sched-start-time" /></label>
        <label className="text-xs text-[#888]">End date<input className={inputCls} type="date" disabled={!date} value={v.end_at?.slice(0, 10) || ""} onChange={(e) => set("end_at", `${e.target.value}T${v.end_at?.slice(11) || "17:00"}`)} data-testid="sched-end-date" /></label>
        <label className="text-xs text-[#888]">End time<input className={inputCls} type="time" disabled={!date} value={v.end_at?.slice(11) || ""} onChange={(e) => set("end_at", `${v.end_at?.slice(0, 10) || date}T${e.target.value}`)} data-testid="sched-end-time" /></label>
      </div>
      <div>
        <p className="mb-1 text-xs text-[#888]">Technicians</p>
        <div className="flex flex-wrap gap-2">
          {techs.map((t) => (
            <button type="button" key={t.id} onClick={() => toggleTech(t.id)} data-testid={`sched-tech-${t.id}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${v.technician_ids.includes(t.id) ? "border-transparent text-black" : "border-[#333] text-[#ccc]"}`}
              style={v.technician_ids.includes(t.id) ? { background: techColor(techs, t.id) } : {}}>{t.name}</button>
          ))}
        </div>
      </div>
      {date && (
        <div className="space-y-1.5 rounded-lg border border-[#2a2a2a] bg-[#141414] p-3 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" className="accent-[#4CCC06]" checked={v.notify_customer} onChange={(e) => set("notify_customer", e.target.checked)} disabled={!hasEmail} data-testid="sched-notify" />Email the customer the appointment details when I save</label>
          <label className="flex items-center gap-2"><input type="checkbox" className="accent-[#4CCC06]" checked={v.remind_customer} onChange={(e) => set("remind_customer", e.target.checked)} disabled={!hasEmail} data-testid="sched-remind" />Email the customer a reminder the day before</label>
          {!hasEmail && <p className="text-xs text-[#FFB800]" data-testid="sched-no-email">Add an email to this customer to send appointment emails.</p>}
        </div>
      )}
    </div>
  );
};

export const schedState = (j) => ({ start_at: j?.start_at || null, end_at: j?.end_at || null, technician_ids: j?.technician_ids || [], notify_customer: false, remind_customer: !!j?.remind_customer });

export const ScheduleDialog = ({ job, techs, customers, onClose, onSaved }) => {
  const [v, setV] = useState(schedState(job));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(schedState(job)); }, [job]);
  if (!job) return null;
  const hasEmail = !!customers?.[job.customer_id]?.email;
  const save = async () => {
    setBusy(true);
    try {
      const j = await api(`/billing/jobs/${job.id}/schedule`, { method: "PATCH", body: v });
      toast.success(v.notify_customer ? (j.confirmation_sent ? "Scheduled. Confirmation emailed to the customer." : "Scheduled, but the email couldn't be sent.") : "Schedule saved");
      onSaved(j); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl border-[#333] bg-[#1A1A1A] text-white" data-testid="schedule-dialog">
        <DialogHeader><DialogTitle>Job #{job.number} · {job.customer_name}</DialogTitle></DialogHeader>
        <p className="-mt-2 text-sm text-[#A0A0A0]">{job.title}</p>
        <ScheduleFields v={v} setV={setV} techs={techs} hasEmail={hasEmail} />
        <div className="flex gap-2">
          <Btn onClick={save} busy={busy} data-testid="sched-save-btn">SAVE SCHEDULE</Btn>
          <Btn variant="outline" onClick={() => window.location.assign(`/billing/job/${job.id}`)} data-testid="sched-open-job-btn">OPEN JOB</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
};
